/**
 * Layout constants consumed as numbers rather than antd seed tokens.
 *
 * Scoped deliberately narrow: only what DataTable needs. (The named type and
 * spacing scales — `typeScale` and `space` below — are the design-system
 * workstream's; they live here too now that they have real consumers.)
 *
 * Table row padding lived as a per-screen `size="small"` coin flip (4 screens
 * small, 15 default). These values are the single source; the theme's Table
 * block applies them so no screen sets `size` directly.
 */
export const density = {
  rowPaddingBlock: 12,
  rowPaddingBlockSM: 8,
  /** Small tables — EVERY `size="small"` antd Table gets this (the console
   *  Leads list, Conference › Assignments, SalesforceExportDialog's member
   *  table, the Match Details drawer): 12px cells and 6px horizontal cell
   *  padding, so eleven columns fit a laptop without a horizontal scroll bar
   *  for the common case on the Batch worksheet, the driver of this change
   *  but not its only recipient (gh-31 D2). */
  cellFontSizeSM: 12,
  cellPaddingInlineSM: 6,
} as const;

/**
 * Named font-size steps (px), scoped narrow for the same reason as `density`
 * above: only what the current heading call sites need, not a full sweep.
 *
 * An audit of inline `fontSize` literals across apps/* and packages/screens
 * found ad-hoc numbers: 11, 12, 13, 16, 28, 64. This scale anchors the named
 * steps to the ones that are heading-shaped:
 *   - `h1` (64) matches apps/portal's StatCallout headline number.
 *   - `h2` (28) matches MetricCard's KPI value.
 *   - `h3` (24) and `h4`/`h5` (20/16) are antd's own Typography.Title
 *     defaults for levels 3-5 (fontSizeHeading3/4/5) — no literal currently
 *     overrides them, so the scale just names the size already rendered.
 *   - `body` (14) is antd's default `fontSize` token (unchanged DM Sans body
 *     copy).
 *   - `caption` (11) matches the small uppercase eyebrow/label text (e.g.
 *     MetricCard's label, StatCallout's eyebrow).
 *
 * The 12/13 literals (secondary `Typography.Text` captions, one dialog
 * label) don't map cleanly onto a step here and are NOT migrated in this
 * pass — see the typography report for the follow-up sweep list.
 */
export const typeScale = {
  h1: 64,
  h2: 28,
  h3: 24,
  h4: 20,
  h5: 16,
  body: 14,
  caption: 11,
} as const;

/**
 * Named spacing steps (px) on a 4px grid, for inline margin/padding/gap.
 *
 * An audit of ~116 inline spacing literals across apps/* and packages/* found
 * the on-grid values 4/8/12/16/24/48 dominate (8 alone is ~40 uses); this scale
 * names exactly those. Off-grid one-offs (2/6/18/20) and a bare `0` reset are
 * deliberately NOT tokenized — they stay literals so a reader is never sent to
 * this scale for a value that is not in it.
 *
 * These are our own inline-style constants; antd's seed spacing (`sizeStep`
 * etc.) is intentionally left alone so component internals are unchanged.
 */
export const space = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 48,
} as const;

/**
 * Named letter-spacing (a.k.a. "tracking") steps for the two places the app
 * tracks type: the uppercase `eyebrow` label (EyebrowLabel) and the small
 * strong `label` text on the batch metric/tag chips. Named `tracking` rather
 * than `letterSpacing` so it doesn't collide with the CSS `letterSpacing`
 * property at call sites. Replaces the bare `'0.12em'` / `0.6` literals.
 */
export const tracking = {
  eyebrow: '0.12em',
  label: 0.6,
} as const;
